import React, { createContext, useEffect, useMemo, useState } from "react";
import { DEFAULT_BREAKPOINTS, DEFAULT_DESKTOP_BREAKPOINT } from "../components/constants";
import { useMatchMediaQueryPreset } from "../hooks/use-match-media-preset";
import { ScreenSizeContextProviderProps, ScreenSizeState, ScreenSizeStateSetters } from "./types";

export const ScreenSizeStateContext = createContext<ScreenSizeState>(undefined);
ScreenSizeStateContext.displayName = "ScreenSizeStateContext";

export const ScreenSizeStateSetterContext = createContext<ScreenSizeStateSetters>(undefined);
ScreenSizeStateSetterContext.displayName = "ScreenSizeStateSetterContext";

/**
 * A context provider that provides a global state for its children sub-tree that indicates the Browser screen size.
 * It allows conditionally rendering UI elements and executing application logic based on this state.
 * It uses the `matchMedia` browser API to identify if the current browser window width qualifies as a desktop
 * or a mobile screen size, based on screen width breakpoints and mobile-to-desktop cut-over threshold configuration.
 *
 * It has the following companion hooks to use its functionality:
 *
 * You would typically only use these:
 *
 * - `useIsDesktop`
 * - `useIsMobile`
 *
 * These are available but won't usually be needed:
 *
 * - `useBreakpoints`
 * - `useSetBreakpoints`
 * - `useDesktopBreakpoint`
 * - `useSetDesktopBreakpoint`
 */
export const ScreenSizeContextProvider = ({
  breakpoints: breakpointsProp = DEFAULT_BREAKPOINTS,
  desktopBreakpoint: desktopBreakpointProp = DEFAULT_DESKTOP_BREAKPOINT,
  defaultScreenSize = "mobile",
  children,
}: ScreenSizeContextProviderProps) => {
  const [breakpoints, setBreakpoints] = useState(breakpointsProp);
  const [desktopBreakpoint, setDesktopBreakpoint] = useState(desktopBreakpointProp);

  const isMobile = useMatchMediaQueryPreset("mobile", breakpoints, desktopBreakpoint, defaultScreenSize === "mobile");

  useEffect(() => {
    setBreakpoints((breakpointsState) => (breakpointsProp !== breakpointsState ? breakpointsProp : breakpointsState));
  }, [breakpointsProp]);

  useEffect(() => {
    setDesktopBreakpoint((desktopBreakpointState) =>
      desktopBreakpointProp !== desktopBreakpointState ? desktopBreakpointProp : desktopBreakpointState
    );
  }, [desktopBreakpointProp]);

  // memoize setters object so that LocalizedTextSetterContext consumers don't re-render unnecessarily
  const stateSetters = useMemo<ScreenSizeStateSetters>(
    () => ({
      setBreakpoints,
      setDesktopBreakpoint,
    }),
    []
  );

  return (
    <ScreenSizeStateContext.Provider value={{ breakpoints, desktopBreakpoint, isDesktop: !isMobile, isMobile }}>
      <ScreenSizeStateSetterContext.Provider value={stateSetters}>{children}</ScreenSizeStateSetterContext.Provider>
    </ScreenSizeStateContext.Provider>
  );
};
